/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/array-type */
import {ExclamationOutlined, SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Button, Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import moment from 'moment';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {APITypeEnum} from '../../services/interface/tableAPIType';
import carbonOffsetAPI from '../../services/modules/OM/carbonOffsetAPI';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import usePlatformStore from '../../store/platformStore/platformStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import {shouldBlockReadOnly} from '../../utils/SubscriptionHelper';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import DropdownConfigProvider from '../DrawerForm/Utils/DropdownConfigProvider';
import CarbonOffsetForm from '../Forms/CarbonOffsetForm';
import {
  CarbonOffsetKey,
  CarbonOffsetLabel,
  carbonOffsetProjectTypes,
  carbonOffsetStageTypes,
  carbonOffsetSteps
} from '../Forms/Constants/CarbonOffset/CarbonOffsetKeyLabel';
import {yearKeyLabel} from '../Forms/Constants/IP/commonKeyLabel';
import {emissionUnits} from '../Forms/interface/unitChange';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import Loader from '../Loader/Loader';

import {FullInfo, makeCheckKeyLabel, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import {
  createBulkRowSelection,
  excludeAcceptedIdsForDelete,
  getBulkSelectedIdsInDataSource,
  useBulkCheckerModals
} from './Utils/TableBulkSelectionBar';
import TableTitleHeader from './Utils/TableTitleHeader';

export interface CarbonOffsetRow {
  _id: string;
  year: string;
  projectName: string;
  projectType: string;
  stageType: string;
  emissionsOffset: number;
  description?: string;
  status?: string;
  comment?: string;
  updatedAt?: string;
}

// Shapes a flattened table row into the drawer form / PUT payload structure
const toFormData = (record: CarbonOffsetRow): any => {
  return {
    _id: record._id,
    [yearKeyLabel.key]: record.year,
    [CarbonOffsetKey.PROJECTS]: [
      {
        [CarbonOffsetKey.PROJECT_NAME]: record.projectName,
        [CarbonOffsetKey.PROJECT_TYPE]: record.projectType,
        [CarbonOffsetKey.STAGE_TYPE]: record.stageType,
        [CarbonOffsetKey.EMISSIONS_OFFSET]: record.emissionsOffset,
        [CarbonOffsetKey.DESCRIPTION]: record.description
      }
    ]
  };
};

const CarbonOffsetTable: React.FC<{
  selectedYear: string;
  statusFilter: string;
  onDataChange: (rows: CarbonOffsetRow[]) => void;
}> = ({selectedYear, statusFilter, onDataChange}) => {
  const companyId = useCompanyStore.getState().companyId;
  const [dataSource, setDataSource] = useState<CarbonOffsetRow[]>([]);

  const [showModel, setShowModel] = useState(false);
  const [showModelInfo, setShowModelInfo] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const bulkSelectedIds = useMemo(
    () => getBulkSelectedIdsInDataSource(selectedRowKeys, dataSource),
    [selectedRowKeys, dataSource]
  );
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);

  const formData = useMemo(
    () => (isEdit && editData ? toFormData(editData) : undefined),
    [isEdit, editData]
  );
  const checkerFormData = useMemo(
    () =>
      formData
        ? {
            ...formData,
            [checkerStatusKey.STATUS]: editData?.status,
            [checkerStatusKey.COMMENT]: editData?.comment
          }
        : undefined,
    [formData]
  );

  const canEdit =
    hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role);
  const canCheck =
    hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role);

  const fetchData = (): void => {
    setIsRefresh(true);
    carbonOffsetAPI(APITypeEnum.GET_ALL, undefined, `${companyId}/${selectedYear}`)
      .then((res) => {
        setDataSource(res ?? []);
      })
      .catch(() => {
        setDataSource([]);
      })
      .finally(() => {
        setIsRefresh(false);
        setRefresh(false);
      });
  };

  useEffect(() => {
    if (selectedYear) {
      fetchData();
    }
  }, [selectedYear]);

  useEffect(() => {
    if (refresh) {
      fetchData();
    }
  }, [refresh]);

  useEffect(() => {
    onDataChange(dataSource);
  }, [dataSource]);

  const filteredDataSource = useMemo(() => {
    return statusFilter
      ? dataSource.filter((row) => row.status === statusFilter)
      : dataSource;
  }, [dataSource, statusFilter]);

  const handleDelete = (_id: string[] | string) => {
    const ids = Array.isArray(_id) ? _id : [_id];
    const carbonOffset = excludeAcceptedIdsForDelete(ids, dataSource);
    if (carbonOffset.length === 0) {
      return;
    }
    carbonOffsetAPI(APITypeEnum.DELETE_ALL, {
      carbonOffset
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {})
      .finally(() => {});
  };

  const bulkChecker = useBulkCheckerModals({
    selectedIds: bulkSelectedIds,
    refresh,
    selectionContextKey: selectedYear,
    onSelectionContextChange: () => {
      setSelectedRowKeys([]);
    },
    getBulkDeleteEligibleCount: (ids) =>
      excludeAcceptedIdsForDelete(ids, dataSource).length,
    onSuccess: () => {
      setSelectedRowKeys([]);
      setRefresh(true);
    },
    onBulkStatusSubmit: async ({ids, status, comment}) =>
      await carbonOffsetAPI(APITypeEnum.STATUS_ALL, {
        carbonOffset: ids,
        status,
        [checkerStatusKey.COMMENT]: comment
      }),
    onBulkDelete: async (ids) => {
      const carbonOffset = excludeAcceptedIdsForDelete(ids, dataSource);
      if (carbonOffset.length === 0) {
        return false;
      }

      return await carbonOffsetAPI(APITypeEnum.DELETE_ALL, {
        carbonOffset
      });
    }
  });

  const handleSelectAll = () => {
    setSelectedRowKeys(filteredDataSource.map((row) => row._id));
  };

  const searchInput = useRef<InputRef>(null);

  const SearchFilter = (
    title: string,
    dataIndex: keyof CarbonOffsetRow
  ): defaultCol & ColumnType<CarbonOffsetRow> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        return (
          record[dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toString().toLowerCase()) ?? false
        );
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const carbonOffsetKeyLabel = [
    {
      key: yearKeyLabel.key,
      label: yearKeyLabel.label,
      type: objTypes.string
    },
    {
      key: CarbonOffsetKey.PROJECT_NAME,
      label: CarbonOffsetLabel.PROJECT_NAME,
      type: objTypes.string
    },
    {
      key: CarbonOffsetKey.PROJECT_TYPE,
      label: CarbonOffsetLabel.PROJECT_TYPE,
      type: objTypes.json,
      format: carbonOffsetProjectTypes
    },
    {
      key: CarbonOffsetKey.STAGE_TYPE,
      label: CarbonOffsetLabel.STAGE_TYPE,
      type: objTypes.json,
      format: carbonOffsetStageTypes
    },
    {
      key: CarbonOffsetKey.EMISSIONS_OFFSET,
      label: CarbonOffsetLabel.EMISSIONS_OFFSET,
      type: objTypes.number,
      unit: emissionUnits.T_CO2e
    },
    {
      key: CarbonOffsetKey.DESCRIPTION,
      label: CarbonOffsetLabel.DESCRIPTION,
      type: objTypes.string
    },
    ...(canCheck
      ? [
          {
            key: checkerStatusKey.STATUS,
            label: checkerStatusLabel.STATUS,
            type: objTypes.status
          },
          {
            key: checkerStatusKey.COMMENT,
            label: checkerStatusLabel.COMMENT,
            type: objTypes.comment
          }
        ]
      : [])
  ];

  const carbonOffsetPreview = [
    {
      key: yearKeyLabel.key,
      label: CarbonOffsetLabel.FISCAL_YEAR,
      type: objTypes.string
    },
    {
      key: CarbonOffsetKey.PROJECTS,
      label: 'Projects',
      type: objTypes.listSummary,
      renderFormat: [
        CarbonOffsetKey.PROJECT_NAME,
        CarbonOffsetKey.EMISSIONS_OFFSET
      ],
      unit: emissionUnits.T_CO2e,
      renderType: 'multi'
    },
    {
      key: CarbonOffsetKey.PROJECTS,
      label: CarbonOffsetLabel.TOTAL_EMISSIONS_OFFSET,
      type: objTypes.listTotal,
      renderFormat: [CarbonOffsetKey.EMISSIONS_OFFSET],
      unit: emissionUnits.T_CO2e
    }
  ];

  const columns: (defaultCol & ColumnsType<CarbonOffsetRow>[0])[] = [
    {
      title: CarbonOffsetLabel.PROJECT_NAME,
      dataIndex: CarbonOffsetKey.PROJECT_NAME,
      ...SearchFilter(CarbonOffsetLabel.PROJECT_NAME, 'projectName')
    },
    {
      title: CarbonOffsetLabel.DESCRIPTION,
      dataIndex: CarbonOffsetKey.DESCRIPTION,
      width: 480,
      onCell: () => ({style: {minWidth: 480}}),
      ...SearchFilter(CarbonOffsetLabel.DESCRIPTION, 'description'),
      render: (value) => value || '-'
    },
    {
      title: CarbonOffsetLabel.EMISSIONS_OFFSET_COLUMN,
      dataIndex: CarbonOffsetKey.EMISSIONS_OFFSET,
      render: (value) => `${formatIndian(value)} ${emissionUnits.T_CO2e}`
    },
    {
      title: CarbonOffsetLabel.LAST_UPDATED,
      dataIndex: CarbonOffsetKey.UPDATED_AT,
      render: (value) => (value ? moment(value).format('DD MMM YYYY') : '-')
    },
    ...(hasUserRole(UserRole.Maker_Role) ||
    hasUserRole(UserRole.Both_role) ||
    hasViewRole(UserRole.View_Only)
      ? [
          {
            title: makeCheckKeyLabel.STATUS_COMMENTS.label,
            dataIndex: 'status',
            editable: true,
            type: 'select',
            render: (value: any, record: any) => {
              const colorText = getStatusColor(value);

              return (
                <div
                  style={{
                    fontWeight: 700,
                    color: colorText.color,
                    cursor: 'pointer'
                  }}
                >
                  {colorText.text}{' '}
                  {value === 'rejected' && record?.comment && (
                    <span className="customComment"> - {record.comment}</span>
                  )}
                </div>
              );
            }
          }
        ]
      : []),
    ...(canCheck
      ? [
          {
            title: makeCheckKeyLabel.STATUS.label,
            dataIndex: 'status',
            editable: true,
            type: 'select',
            fixed: 'right' as const,
            render: (value: any, record: any) => {
              const colorText = getStatusColor(value);

              return (
                <Tag
                  onClick={() => {
                    if (shouldBlockReadOnly()) {
                      usePlatformStore
                        .getState()
                        .setOpenOnFreeTrialMessageModel(true);

                      return;
                    }
                    setShowModelInfo(true);
                    setEditData(record);
                    setIsEdit(true);
                  }}
                  color={colorText.color}
                  style={{
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  {colorText.text}
                </Tag>
              );
            }
          },
          {
            title: FullInfo.title,
            dataIndex: 'FullInfo',
            editable: true,
            type: 'select',
            fixed: 'right' as const,
            render: (_: any, record: any) => {
              return (
                <Button
                  onClick={() => {
                    setShowModelInfo(true);
                    setEditData(record);
                    setIsEdit(true);
                  }}
                  shape="circle"
                  style={{
                    marginRight: 16,
                    cursor: 'pointer',
                    color: '#435B37',
                    background: '#F5FBF3',
                    border: 0
                  }}
                >
                  <ExclamationOutlined />
                </Button>
              );
            }
          }
        ]
      : []),
    ...(canEdit
      ? [
          {
            title: Title.actionTitle,
            dataIndex: 'operation',
            type: '' as const,
            fixed: 'right' as const,
            width: '200px',
            render: (value: any, record: any) => {
              return dataSource.length >= 1 ? (
                <ActionsButton
                  record={record}
                  setShowModel={setShowModel}
                  setEditData={setEditData}
                  setIsEdit={setIsEdit}
                  handleDelete={handleDelete}
                />
              ) : null;
            }
          }
        ]
      : [])
  ];

  return (
    <DropdownConfigProvider>
      <div>
        <TableTitleHeader
          title="Carbon Offset Projects"
          filter={{facilityId: '', selectedYear}}
          addButton={{
            setShowModel,
            setIsEdit,
            buttonTitle: 'Add Offset'
          }}
          sampleDownload={{href: ''}}
          jobs={{setShowJobs: null, uploadType: ''}}
          deleteButton={{selectedRowKeys, handleDelete}}
          refresh={{setRefresh}}
          showAddOnly={true}
          onlyYear={true}
          shouldEdit={canEdit}
          onSelectAll={canEdit ? handleSelectAll : undefined}
          allSelectableCount={filteredDataSource.length}
          onDeselectAll={() => {
            setSelectedRowKeys([]);
          }}
          onChangeStatus={canEdit ? bulkChecker.openChangeStatus : undefined}
          onBulkDelete={canEdit ? bulkChecker.openBulkDelete : undefined}
        />
        {bulkChecker.bulkCheckerModals}

        <TableAnt
          rowSelection={createBulkRowSelection<CarbonOffsetRow>({
            selectedRowKeys,
            setSelectedRowKeys,
            onSelectAll: handleSelectAll,
            onDeselectAll: () => {
              setSelectedRowKeys([]);
            },
            enabled: canEdit
          })}
          rowKey="_id"
          dataSource={filteredDataSource}
          columns={columns as any}
          className="carbonOffsetTable"
          scroll={{x: 'auto'}}
          loading={{
            indicator: <Loader />,
            spinning: isRefresh
          }}
        />

        <DrawerForm
          open={showModel}
          onCancel={() => {
            setShowModel(false);
          }}
          setRefresh={setRefresh}
          data={formData}
          FormComponent={<CarbonOffsetForm data={formData} />}
          API={carbonOffsetAPI}
          idKey={`${companyId}/${selectedYear}`}
          table={true}
          onlyYear={true}
          stepsTitle={carbonOffsetSteps}
          formPreview={carbonOffsetPreview}
        />
      </div>
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        title="All Data"
        data={checkerFormData}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={carbonOffsetKeyLabel} />
        }
        stepsTitle={['Status & Comments']}
        isChecker={true}
        API={carbonOffsetAPI}
        isAction={false}
      />
    </DropdownConfigProvider>
  );
};

export default CarbonOffsetTable;
